import { AccountSelector } from '../AccountSelector';
import { FundSelector } from '../FundSelector';
import { TypeSelector } from '../TypeSelector';
import { YearSelector } from '../YearSelector';
import style from './DownloadFilesSelectFilters.module.scss';
import { Input } from 'antd';
import downloadFilesApi from 'api/download-files';
import { Alert } from 'api/utils';
import { AxiosError } from 'axios';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { Svgicon } from 'components/Svgicon/Svgicon';
import {
  GlobalElementBusContext,
  ModalActivationContext,
} from 'contexts';
import {
  type FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState } from 'react';
import { useDispatch,
  useSelector } from 'react-redux';
import { type AppDispatch } from 'store';
import { type DocumentsStoreState,
  fetchCategories,
  type Filters,
  type FilterStoreState,
  type OrganizationsStoreState,
  type PeriodFilters,
  resetLatestDocuments } from 'store/slices';
import {type NestedOrganization, type NestedYear } from 'types';

/**
 * Renders the relevant selectors and handles validation of inputs
 */

const DownloadFilesSelectFilters: FC = () => {
  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;

  const periodFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.usualDocumentsFilter) as PeriodFilters | null;

  const containerRef = useRef(null);
  const {
    addRef,
  } = useContext(GlobalElementBusContext);

  const { activeDocumentGroupName,
    toggleDownloadsModal } = useContext(ModalActivationContext);

  const { displayedOrganization, displayedOrganizationWithPermissions } = useSelector<{
    organizations: OrganizationsStoreState,
  }>((state) => state.organizations) as OrganizationsStoreState;

  const {
    displayType,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;

  const [
    disabled,
    setDisabled,
  ] = useState(true);
  const [
    isSent,
    setIsSent,
  ] = useState(false);
  const dispatch: AppDispatch = useDispatch();

  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<NestedYear[]>([]);

  const userEmail = sessionStorage.getItem('email') || '';

  // Helper functions
  const getCommonFundIds = () => {
    if (displayedOrganization && displayedOrganizationWithPermissions) {
      const orgFundIds = displayedOrganization?.funds.map((fund) => fund.fundId);
      return displayedOrganizationWithPermissions?.funds
        .filter((fund) => orgFundIds?.includes(fund.fundId))
        .map((fund) => fund.fundId);
    }

    return [];
  };

  const getCommonAccounts = (fundIds: string[]) => {
    if (displayedOrganization && displayedOrganizationWithPermissions) {
      const orgAccountIds = displayedOrganization?.funds
        .flatMap((fund) => fund.accounts.map((acc) => acc.accountId));
      return displayedOrganizationWithPermissions?.funds
        .filter((fund) => fundIds.includes(fund.fundId))
        .flatMap((fund) => fund.accounts)
        .filter((acc) => orgAccountIds?.includes(acc.accountId));
    }

    return [];
  };

  useEffect(() => {
    setDisabled(!selectedFundIds.length || !selectedAccountIds.length ||
        !selectedFileTypes.length || !selectedPeriods.length || !userEmail);
  }, [selectedAccountIds, selectedFileTypes, selectedFundIds, selectedPeriods, userEmail]);

  const availableAccounts = useMemo(() => {
    return displayedOrganizationWithPermissions ? displayedOrganizationWithPermissions.funds
      .filter((fund) => selectedFundIds.includes(fund.fundId))
      .flatMap((fund) => fund.accounts) : [];
  }, [displayedOrganizationWithPermissions, selectedFundIds]);

  const availableFileTypes = useMemo(() => {
    const accounts = availableAccounts.filter((acc) =>
      selectedAccountIds.includes(acc.accountId),
    );

    const allPermissions = accounts.flatMap((acc) => acc.permissions);

    return allPermissions.filter(
      (permission, index, self) =>
        index === self.findIndex((per) => per.code === permission.code),
    );
  }, [availableAccounts, selectedAccountIds]);

  const availablePeriods = useMemo(() => {
    return availableAccounts
      .filter((acc) => selectedAccountIds.includes(acc.accountId))
      .flatMap((acc) => acc.yearsAndQuarters)
      .reduce((acc: NestedYear[], year) => {
        const existing = acc.find((y) => y.year === year.year);
        if (existing) {
          existing.quarters = [...new Set([...existing.quarters, ...year.quarters])];
        } else {
          acc.push({ ...year, quarters: [...year.quarters] });
        }

        return acc;
      }, []);
  }, [availableAccounts, selectedAccountIds]);

  useEffect(() => {
    if (!activeDocumentGroupName ||
      !displayedOrganizationWithPermissions || !displayedOrganization) {
      return;
    }

    const commonFundIds = getCommonFundIds();
    setSelectedFundIds(commonFundIds);

    const commonAccounts = getCommonAccounts(commonFundIds);
    setSelectedAccountIds(commonAccounts.map((acc) => acc.accountId));

    if (displayType.id === 'type') {
      if (commonAccounts.flatMap((acc) => acc.permissions)
        .some((ft) => ft.code === activeDocumentGroupName)) {
        setSelectedFileTypes([activeDocumentGroupName]);
        setSelectedPeriods(commonAccounts.flatMap((acc) => acc.yearsAndQuarters));
      }
    } else {
      setSelectedFileTypes(commonAccounts.flatMap((acc) => acc.permissions)
        .map((type) => type.code));

      const [yearStr, quarterStr] = activeDocumentGroupName.split('/');
      const year = yearStr;
      const periodExists = commonAccounts
        .flatMap((acc) => acc.yearsAndQuarters)
        .some((per) => {
          const yearMatches = String(per.year) === year.toString();
          const quarters = Array.isArray(per.quarters) ? per.quarters : [per.quarters];
          const quarterMatches = quarters.includes(quarterStr);

          return yearMatches && quarterMatches;
        });

      if (periodExists) {
        setSelectedPeriods([{ quarters: [quarterStr], year: Number(yearStr) }]);
      }
    }
  }, [
    activeDocumentGroupName,
    displayType,
    displayedOrganization,
    displayedOrganizationWithPermissions,
  ]);

  useEffect(() => {
    const validAccountIds = availableAccounts.map((acc) => acc.accountId);
    const filteredAccountIds = selectedAccountIds.filter((id) => validAccountIds.includes(id));

    if (filteredAccountIds.length !== selectedAccountIds.length) {
      setSelectedAccountIds(filteredAccountIds);
    }
  }, [availableAccounts, selectedFundIds]);

  useEffect(() => {
    const validFileTypes = availableFileTypes.map((perm) => perm.code);
    const filteredFileTypes = selectedFileTypes.filter((type) => validFileTypes.includes(type));

    if (filteredFileTypes.length !== selectedFileTypes.length) {
      setSelectedFileTypes(filteredFileTypes);
    }

    const filteredPeriods = selectedPeriods.filter((selectedPeriod) => {
      const availableYear = availablePeriods.find((ap) => ap.year === selectedPeriod.year);

      if (!availableYear) {
        return false;
      }

      return selectedPeriod.quarters.every((qua) => availableYear.quarters.includes(qua));
    });

    if (filteredPeriods.length !== selectedPeriods.length) {
      setSelectedPeriods(filteredPeriods);
    }
  }, [availableFileTypes, availablePeriods, selectedAccountIds]);

  const onSend = async () => {
    try {
      await downloadFilesApi.sendDownloadFilesRequest({
        accountIds: selectedAccountIds,
        email: userEmail,
        firmId: displayedOrganizationWithPermissions?.organizationId || '',
        fundIds: selectedFundIds,
        types: selectedFileTypes,
        years: selectedPeriods,
      });

      if (activeFilters) {
        dispatch(fetchCategories({
          ...activeFilters,
          displayType: displayType.id,
          firmId: activeFilters.organizationId,
          quarters: periodFilters?.selectedQuarters,
        }));

        dispatch(resetLatestDocuments());
      }

      setIsSent(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        Alert(error.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      addRef(containerRef);
    }
  }, [
    addRef,
    containerRef,
  ]);

  const selectors = () => <>
    <span className='text-title-3 font-medium'>
      Export documents
    </span>
    <FundSelector
      availableData={displayedOrganizationWithPermissions as NestedOrganization}
      selectedFundIds={selectedFundIds}
      setSelectedFundIds={setSelectedFundIds}
      style={{width: '100%'}}
    />
    <AccountSelector
      availableAccounts={availableAccounts}
      selectedAccountIds={selectedAccountIds}
      setSelectedAccountIds={setSelectedAccountIds}
      style={{width: '100%'}}
    />
    <TypeSelector
      availableFileTypes={availableFileTypes}
      selectedFileTypes={selectedFileTypes}
      setSelectedFileTypes={setSelectedFileTypes}
      style={{width: '100%'}}
    />
    <YearSelector
      availablePeriods={availablePeriods}
      selectedPeriods={selectedPeriods}
      setSelectedPeriods={setSelectedPeriods}
    />
    <div className='flex w-full flex-col gap-0.125'>
      <div className='text-font-3 font-medium'>
        Email
      </div>
      <Input
        className={style.inputBox}
        data-test='download-files-select-filters-email-input'
        disabled
        placeholder='example@email.com'
        type='text'
        value={userEmail}
      />
    </div>
    <div className='w-full'>
      <Button
        className='!h-11 w-full'
        data-test='export-documents'
        disabled={disabled}
        onClick={onSend}
        size='bigger-medium'
        squared
      >
        {'Continue'}
      </Button>
    </div>
  </>;

  const onBackToDashboard = () => {
    toggleDownloadsModal(false, null);
    setIsSent(false);
  };

  const documentsSent = () => <div className='flex flex-col items-center gap-2 dt:px-4 dt:py-[10em]'>
    <Svgicon className='size-4 text-main-700' id='check-circle-2f' />
    <div className='flex flex-col items-center gap-1'>
      <div className='flex flex-col items-center gap-0.25'>
        <span className='flex flex-col items-center gap-0.25 text-title-3'>
          Documents sent to<span className='text-main-700'>{userEmail}</span>
        </span>
        <span className='text-font-2 text-black-700'>Check your email.</span>
      </div>
      <Button onClick={onBackToDashboard}>Back to dashboard</Button>
    </div>
  </div>;

  return (
    <div className={style.container} data-test='download-files-select-filters' ref={containerRef}>
      {isSent ? documentsSent() : selectors()}
    </div>
  );
};

export { DownloadFilesSelectFilters};
